import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const getSessionId = () => {
  let sessionId = localStorage.getItem('cart_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem('cart_session_id', sessionId);
  }
  return sessionId;
};

// Helper: Generate item key for variant-aware cart operations
const getItemKey = (item) => {
  let key = item.product_id;
  if (item.selected_size) {
    key += `_${item.selected_size}`;
  }
  if (item.selected_color) {
    key += `_${item.selected_color}`;
  }
  return key;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const sessionIdRef = useRef(getSessionId());
  const sessionId = sessionIdRef.current;

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1, selectedSize = null, selectedColor = null) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/cart/${sessionId}/items`, {
        product_id: productId,
        quantity,
        selected_size: selectedSize,
        selected_color: selectedColor
      });
      setCart(response.data);
      return { success: true };
    } catch (err) {
      console.error('Error adding to cart:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to add item' };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const updateQuantity = useCallback(async (itemKey, quantity) => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/cart/${sessionId}/items/${encodeURIComponent(itemKey)}?quantity=${quantity}`);
      setCart(response.data);
    } catch (err) {
      console.error('Error updating cart:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const removeFromCart = useCallback(async (itemKey) => {
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/cart/${sessionId}/items/${encodeURIComponent(itemKey)}`);
      setCart(response.data);
    } catch (err) {
      console.error('Error removing from cart:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const clearCart = useCallback(async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart({ items: [] });
    } catch (err) {
      console.error('Error clearing cart:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const cartItems = cart.items;
  
  const getCartTotal = useCallback(() => {
    return cartItems?.reduce((total, item) => total + (item.price * item.quantity), 0) || 0;
  }, [cartItems]);

  const getCartCount = useCallback(() => {
    return cartItems?.reduce((count, item) => count + item.quantity, 0) || 0;
  }, [cartItems]);

  // Split into smaller memos
  const cartState = useMemo(() => ({ cart, loading, sessionId }), [cart, loading, sessionId]);
  const cartActions = useMemo(() => ({ 
    addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, getItemKey
  }), [addToCart, updateQuantity, removeFromCart, clearCart, fetchCart]);
  const cartHelpers = useMemo(() => ({ getCartTotal, getCartCount }), [getCartTotal, getCartCount]);

  const contextValue = useMemo(() => ({
    ...cartState,
    ...cartActions,
    ...cartHelpers
  }), [cartState, cartActions, cartHelpers]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
